// Province type removed — now string for multi-country support
import { ToastTypes } from "@/modules/common/components/toast";
import { StoreProduct, HttpTypes } from "@medusajs/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { clearCountryCookie } from "@/lib/util/country";

interface CartStore {
  isCartOpen: boolean;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

interface ProductModalStore {
  isModalOpen: boolean;
  selectedProduct:
    | (StoreProduct & { price: string; originalPrice?: string; stock?: number })
    | null;
  openModal: (product: ProductModalStore["selectedProduct"]) => void;
  closeModal: () => void;
}

interface ToastStore {
  isToastVisible: boolean;
  toastMessage: string;
  toastExtraInfo: { action: { label: string; onClick: () => void } } | null;
  toastType: ToastTypes;
  toastHideTime: number;
  showToast: (
    message: string,
    type?: ToastTypes,
    extraInfo?: { action: { label: string; onClick: () => void } },
    hideTime?: number
  ) => void;
  hideToast: () => void;
}

export const useCartStore = create<CartStore>((set) => ({
  isCartOpen: false,
  toggleCart: () => set((state) => ({ isCartOpen: !state.isCartOpen })),
  openCart: () => set({ isCartOpen: true }),
  closeCart: () => set({ isCartOpen: false }),
}));

export const useProductModalStore = create<ProductModalStore>((set) => ({
  isModalOpen: false,
  selectedProduct: null,
  openModal: (product) => set({ isModalOpen: true, selectedProduct: product }),
  closeModal: () => set({ isModalOpen: false, selectedProduct: null }),
}));

export const useToastStore = create<ToastStore>((set) => ({
  isToastVisible: false,
  toastMessage: "",
  toastType: "ok",
  toastExtraInfo: null,
  toastHideTime: 3000,
  showToast: (message, type = "ok", extraInfo, hideTime = 3000) =>
    set({
      isToastVisible: true,
      toastMessage: message,
      toastType: type,
      toastExtraInfo: extraInfo,
      toastHideTime: hideTime,
    }),
  hideToast: () => set({ isToastVisible: false, toastMessage: "" }),
}));

interface LogOutModalStore {
  isOpen: boolean;
  showModal: () => void;
  hideModal: () => void;
}

export const useLogOutModalStore = create<LogOutModalStore>((set) => ({
  isOpen: false,
  showModal: () => set({ isOpen: true }),
  hideModal: () => set({ isOpen: false }),
}));

interface CurrencyStore {
  currencyCode: string;
  setCurrency: (code: string) => void;
}

export const DEFAULT_CURRENCY_CODE = "EUR";
export const CODE_URL_PARAM = "c";
export const useCurrencyStore = create<CurrencyStore>()(
  persist(
    (set) => ({
      currencyCode: DEFAULT_CURRENCY_CODE,
      setCurrency: (code) => set({ currencyCode: code }),
    }),
    {
      name: "currency-storage",
      onRehydrateStorage: () => (state) => {
        const params = new URLSearchParams(window.location.search);
        const urlCurrency = params.get(CODE_URL_PARAM);

        // Si hay moneda en la URL y es diferente a la del storage, actualizamos el store
        if (urlCurrency && state && state.currencyCode !== urlCurrency) {
          state.setCurrency(urlCurrency);
        }
      },
    }
  )
);

interface LocationStore {
  isOpen?: boolean;
  setOpenModal: (open: boolean) => void;
  province?: string;
  provinceName?: string;
  municipality?: string;
  setProvince: (province: string, provinceName: string) => void;
  setMunicipality: (municipality: string) => void;
  hasSelectedLocation: boolean;
  setHasSelectedLocation: (value: boolean) => void;
  isProvinceGuardBlocking: boolean;
  setProvinceGuardBlocking: (value: boolean) => void;
  clearLocation: () => void;
}

export const useLocationStore = create<LocationStore>()(
  persist(
    (set) => ({
      isOpen: false,
      setOpenModal: (open) => set({ isOpen: open }),
      province: undefined,
      provinceName: undefined,
      municipality: undefined,
      setProvince: (province, provinceName) => set({ province, provinceName }),
      setMunicipality: (municipality) => set({ municipality }),
      hasSelectedLocation: false,
      setHasSelectedLocation: (value) => set({ hasSelectedLocation: value }),
      isProvinceGuardBlocking: false,
      setProvinceGuardBlocking: (value) => set({ isProvinceGuardBlocking: value }),
      clearLocation: () =>
        set({
          province: undefined,
          provinceName: undefined,
          municipality: undefined,
          hasSelectedLocation: false,
          isProvinceGuardBlocking: false,
        }),
    }),
    {
      name: "location-storage",
      partialize: (state) => ({
        province: state.province,
        provinceName: state.provinceName,
        municipality: state.municipality,
        hasSelectedLocation: state.hasSelectedLocation,
      }),
    }
  )
);

// Region Store - For managing selected region from Medusa backend
interface RegionStore {
  regionId: string | null;
  region: HttpTypes.StoreRegion | null;
  setRegion: (region: HttpTypes.StoreRegion | null) => void;
  isRegionModalOpen: boolean;
  setRegionModalOpen: (open: boolean) => void;
  hasSelectedRegion: boolean;
  setHasSelectedRegion: (value: boolean) => void;
  isRegionGuardBlocking: boolean;
  setRegionGuardBlocking: (value: boolean) => void;
  countryCode: string | null;
  setCountryCode: (code: string | null) => void;
  isMaintenanceMode: boolean;
  setIsMaintenanceMode: (value: boolean) => void;
  clearRegion: () => void;
}

export const useRegionStore = create<RegionStore>()(
  persist(
    (set) => ({
      regionId: null,
      region: null,
      setRegion: (region) =>
        set(region ? { regionId: region.id, region, hasSelectedRegion: true } : { regionId: null, region: null, hasSelectedRegion: false }),
      isRegionModalOpen: false,
      setRegionModalOpen: (open) => set({ isRegionModalOpen: open }),
      hasSelectedRegion: false,
      setHasSelectedRegion: (value) => set({ hasSelectedRegion: value }),
      isRegionGuardBlocking: false,
      setRegionGuardBlocking: (value) => set({ isRegionGuardBlocking: value }),
      countryCode: null,
      setCountryCode: (code) => set({ countryCode: code }),
      isMaintenanceMode: false,
      setIsMaintenanceMode: (value) => set({ isMaintenanceMode: value }),
      clearRegion: () => {
        clearCountryCookie();
        set({
          regionId: null,
          region: null,
          hasSelectedRegion: false,
          countryCode: null,
          isRegionGuardBlocking: false,
          isMaintenanceMode: false,
        });
      },
    }),
    {
      name: "region-storage",
      partialize: (state) => ({
        regionId: state.regionId,
        region: state.region,
        hasSelectedRegion: state.hasSelectedRegion,
        countryCode: state.countryCode,
      }),
    }
  )
);


